import axios from 'axios';

const API_URL = process.env.REACT_APP_API_SERVER_ENDPOINT;

// axios 인스턴스를 생성하여 모든 요청에 공통 구성 적용
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 아래의 모든 API 함수는 api 인스턴스를 사용
const getTestData = () => api.get('/test');
const getSite = (memberId) => api.get('/get-site', { params: { memberId } });
const getSitePages = (memberId) =>
  api.get('/get-pages', { params: { memberId } });
const getSiteSubs = (pageId) =>
  api.get('/get-site-subs', { params: { pageId } });
const getConfirmedLatestYear = () => api.get('/get-confirmed-latest-year');
const getPropertySubs = (memberId, year, month) =>
  api.get('/get-property-subs', {
    params: { memberId, year, month },
  });
const getAuthorization = (email, password) =>
  api.post('/authenticate', { email, password });
const getMemberInfo = (memberId) =>
  api.get('/get-member-info', { params: { memberId } });
const getAllMembers = () => api.get('/get-all-members');
const updateMemberInfo = (memberId, data) =>
  api.post('/update-member-info', { data, memberId });
const saveHTMLContent = (id, style, script, html) =>
  api.post('/save-html-content', {
    id,
    style,
    script,
    html,
  });
const saveObjects = (id, objects) => api.post('/save-objects', { id, objects });
const getLocale = (memberId, langs = ['ko', 'en']) => {
  console.log('Calling getLocale with langs:', langs, 'memberId:', memberId);
  return api.get('/getLocale', {
    params: {
      member_id: memberId,
      langs,
    },
  });
};
const fetchLocale = (memberId, keycode) =>
  api.get('/fetch-locale', {
    params: { member_id: memberId, keycode },
  });
const saveLocale = (memberId, keycode, localeData) =>
  api.post('/save-locale', {
    member_id: memberId,
    keycode,
    localeData,
  });
const deleteLocale = (memberId, keycode) =>
  api.delete('/delete-locale', {
    data: {
      member_id: memberId,
      keycode,
    },
  });
const updateLocale = (member_id, code, ko, en) =>
  api.post('/update-locale', {
    member_id,
    code,
    ko,
    en,
  });
const saveSEO = (id, seo) => api.post('/save-seo', { id, seo });
const changePassword = (userId, oldPassword, newPassword) =>
  api.post('/change-password', {
    userId,
    oldPassword,
    newPassword,
  });
const getTheme = (themeId) => api.get(`/get-theme/${themeId}`);
const getAvailableThemes = () => api.get('/get-available-themes');
export const updateThemeForSite = (siteId, themeId) =>
  api.post('/update-theme-for-site', {
    siteId,
    themeId,
  });
export const saveEditorPage = (pageId, siteSubs) =>
  api.post('/save-editorpage', {
    pageId,
    siteSubs,
  });

export const deleteSiteSub = (id) => api.post('/delete-site-sub', { id });

// 전역 레이아웃(헤더/푸터) 업데이트
export const updateGlobalLayout = (siteId, headerType, footerType) =>
  api.post('/update-global-layout', {
    siteId,
    globalHeaderType: headerType,
    globalFooterType: footerType,
  });

// 헤더 설정 업데이트 (타이틀 모드, 이미지 등)
export const updateHeaderSettings = (siteId, settings) =>
  api.post('/update-header-settings', {
    siteId,
    ...settings,
  });

// 특정 SiteSub에 연결된 부동산 매물 목록 가져오기
const getSiteSubProperties = (siteSubId) =>
  api.get(`/get-site-sub-properties/${siteSubId}`);

// 부동산 매물 검색
const searchProperties = (params) =>
  api.get('/search-properties', { params }); // params: { q, price_lte, price_gte, limit, offset }

// SiteSub에 연결된 부동산 매물 목록 업데이트
const updateSiteSubProperties = (siteSubId, properties) =>
  api.post('/update-site-sub-properties', { siteSubId, properties });

// 동네 목록 가져오기
const getNeighborhoods = () => api.get('/neighborhoods');

// 새 매물 생성 및 SiteSub 연결
const createPropertyAndLink = (siteSubId, propertyData) =>
  api.post('/create-property-and-link', { siteSubId, propertyData });

// 로그인/계정 관련 추가 API (LoginService 대체용)
// 서버 미구현 API도 인터페이스를 맞춰둠
const deregisterAccount = (data) => api.post('/deregister', data);
const loginViaFB = (fbId, email, firstName, lastName) =>
  api.post('/authenticate-fb', { fbId, email, firstName, lastName });
const signup = (data) => api.post('/new-account', data);
const resetPassword = (email) => api.post('/reset-password', { email });

// 이미지 업로드 API
// 개발 환경에서는 CRA 프록시를 사용하고, 프로덕션에서는 API 서버 사용
const uploadImage = (file, folder = 'uploads') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('folder', folder);

  // 개발 환경에서는 현재 호스트의 /api/upload 사용 (setupProxy.js에서 처리)
  // 프로덕션에서는 API 서버 사용
  const isDevelopment = process.env.NODE_ENV === 'development';

  if (isDevelopment) {
    // 개발 환경: CRA 개발 서버의 setupProxy가 처리
    return axios.post('/api/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  // 프로덕션: API 서버로 전송
  return api.post('/api/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

// --- Blog API ---
const getBlogConfig = (siteId) => api.get(`/blog/${siteId}/config`);
const saveBlogConfig = (siteId, settings) => api.post(`/blog/${siteId}/config`, { settings });
const getBlogPosts = (siteId, params) => api.get(`/blog/${siteId}/posts`, { params });
const getBlogPost = (siteId, slug) => api.get(`/blog/${siteId}/posts/${slug}`);
const createBlogPost = (data) => api.post('/blog/posts', data);
const updateBlogPost = (id, data) => api.put(`/blog/posts/${id}`, data);
const deleteBlogPost = (id) => api.delete(`/blog/posts/${id}`);

// --- Super Admin API (requires info@theadjoa.com) ---
const getSitesWithMembers = () => api.get('/get-sites-with-members');
const getMembersWithoutSites = () => api.get('/get-members-without-sites');
const createSiteForMember = (memberId, title, subtitle) =>
  api.post('/create-site-for-member', { memberId, title, subtitle });

// 모든 함수를 객체로 내보냄
export {
  getTestData,
  getSite,
  getSitePages,
  getSiteSubs,
  getConfirmedLatestYear,
  getPropertySubs,
  getAuthorization,
  getMemberInfo,
  getAllMembers,
  updateMemberInfo,
  // writeFilePromise, // 프론트엔드에서 사용 불가능하므로 제외
  saveHTMLContent,
  saveObjects,
  getLocale,
  fetchLocale,
  saveLocale,
  deleteLocale,
  updateLocale,
  saveSEO,
  changePassword,
  getTheme,
  getAvailableThemes,
  getSiteSubProperties,
  searchProperties,
  updateSiteSubProperties,
  getNeighborhoods,
  createPropertyAndLink,
  deregisterAccount,
  loginViaFB,
  signup,
  resetPassword,
  uploadImage,
  getBlogConfig,
  saveBlogConfig,
  getBlogPosts,
  getBlogPost,
  createBlogPost,
  updateBlogPost,
  deleteBlogPost,
  // Super Admin APIs
  getSitesWithMembers,
  getMembersWithoutSites,
  createSiteForMember,
};
